import React, { useEffect, useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ArrowUp } from "lucide-react";
import { Toaster } from "./components/ui/sonner";
import Navbar from "./components/layout/Navbar";
import Footer from "./components/layout/Footer";
import QuoteDialog from "./components/QuoteDialog";
import { CTASection, WhatsAppButton } from "./components/shared/Shared";
import { LanguageProvider, useLang } from "./context/LanguageContext";
import Home from "./pages/Home";
import { About, Group } from "./pages/About";
import { Offering, Industry } from "./pages/Detail";
import { Works, Blogs } from "./pages/Content";
import { Careers } from "./pages/Careers";
import { Contact } from "./pages/Contact";
import { Admin } from "./pages/Admin";
import { Privacy } from "./pages/Privacy";
import CookieConsent from "./components/CookieConsent";

const TITLES = {
  en: { "/": "Your Innovation Partner", "/about": "About Us", "/group": "Jonaren Group", "/works": "Works", "/blogs": "Blogs & Insights", "/careers": "Careers", "/contact": "Contact Us", "/privacy": "Privacy & Cookie Policy" },
  ar: { "/": "شريكك في الابتكار", "/about": "من نحن", "/group": "مجموعة جونارين", "/works": "أعمالنا", "/blogs": "المدونة", "/careers": "الوظائف", "/contact": "اتصل بنا", "/privacy": "سياسة الخصوصية" },
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  const { lang, c } = useLang();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    const base = TITLES[lang][pathname];
    const slug = pathname.split("/")[2];
    const detail = pathname.startsWith("/offerings/") ? c.ALL_OFFERINGS.find((o) => o.slug === slug)?.title : pathname.startsWith("/industries/") ? c.INDUSTRIES.find((i) => i.slug === slug)?.title : null;
    document.title = `${detail || base || ""}${detail || base ? " | " : ""}${c.COMPANY.name}`;
  }, [pathname, lang, c]);
  return null;
};

const BackToTop = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <button type="button" aria-label="Back to top" data-testid="back-to-top" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className={`fixed bottom-6 end-6 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-brand-orange text-white shadow-lg shadow-brand-orange/30 transition-all duration-300 hover:bg-orange-600 ${show ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-4 opacity-0"}`}>
      <ArrowUp className="h-5 w-5" />
    </button>
  );
};

const SiteLayout = () => {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const openQuote = () => setQuoteOpen(true);
  const { isRTL } = useLang();
  return (
    <div className={`min-h-screen bg-white text-slate-800 antialiased ${isRTL ? "font-arabic" : "font-body"}`}>
      <ScrollToTop />
      <Navbar onQuote={openQuote} />
      <main>
        <Routes>
          <Route path="/" element={<Home onQuote={openQuote} />} />
          <Route path="/about" element={<About />} />
          <Route path="/group" element={<Group />} />
          <Route path="/offerings/:slug" element={<Offering onQuote={openQuote} />} />
          <Route path="/industries/:slug" element={<Industry onQuote={openQuote} />} />
          <Route path="/works" element={<Works />} />
          <Route path="/blogs" element={<Blogs />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="*" element={<Home onQuote={openQuote} />} />
        </Routes>
      </main>
      <CTASection onQuote={openQuote} />
      <Footer />
      <WhatsAppButton />
      <BackToTop />
      <CookieConsent />
      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
    </div>
  );
};

const Root = () => {
  const { isRTL } = useLang();
  return (
    <>
      <Routes>
        <Route path="/admin/*" element={<Admin />} />
        <Route path="*" element={<SiteLayout />} />
      </Routes>
      <Toaster position={isRTL ? "top-left" : "top-right"} richColors closeButton />
    </>
  );
};

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <BrowserRouter>
          <Root />
        </BrowserRouter>
      </LanguageProvider>
    </div>
  );
}

export default App;
